import { useRef, useState } from 'react'
import { Paperclip } from 'lucide-react'
import { validatePoFile, type FileValidationError } from '@/lib/files'
import { cn } from '@/lib/utils'
import { FileChip } from './file-chip'

/**
 * Single-file drag-drop zone for the purchase order document. Client-side
 * validation mirrors the API (extension allowlist, 4 MB); rejections are
 * announced inline with the reason — nothing fails silently. Dropping a
 * second file replaces the first (one PO per request, by contract).
 */
export function FileDropZone({
  file,
  onChange,
}: {
  file: File | null
  onChange: (file: File | null) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [rejection, setRejection] = useState<FileValidationError | null>(null)

  function accept(incoming: File | undefined) {
    if (!incoming) return
    const error = validatePoFile(incoming)
    setRejection(error)
    if (!error) onChange(incoming)
  }

  return (
    <div>
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          accept(Array.from(event.dataTransfer.files)[0])
        }}
        className={cn(
          'rounded-lg border border-dashed px-6 py-8 text-center transition-colors duration-[180ms] ease-editorial',
          dragging ? 'border-brand bg-brandTint/40' : 'border-rule bg-cream/60',
        )}
      >
        <Paperclip aria-hidden="true" className="mx-auto size-5 text-mute" />
        <p className="mt-3 text-sm text-inkMid">
          Drag your PO here, or{' '}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="rounded-sm font-semibold text-brand underline-offset-4 hover:underline"
          >
            browse your computer
          </button>
        </p>
        <p className="mt-1.5 font-mono text-[11px] text-mute">
          one file · 4 MB max · pdf, images, office docs, text, zip
        </p>
        <input
          ref={inputRef}
          type="file"
          aria-label="Attach your purchase order"
          className="sr-only"
          onChange={(event) => {
            accept(Array.from(event.target.files ?? [])[0])
            event.target.value = ''
          }}
        />
      </div>

      {rejection ? (
        <p role="alert" className="mt-2.5 text-xs font-medium text-rust">
          {rejection.name}: {rejection.reason}.
        </p>
      ) : null}

      {file ? (
        <div className="mt-3">
          <FileChip file={file} onRemove={() => onChange(null)} />
        </div>
      ) : null}
    </div>
  )
}
